import React, { useEffect, useState } from 'react';

/**
 * Time Component
 * 
 * This component displays the elapsed time since a given start time.
 * It updates every second and includes a flashing colon effect in the time display.
 * 
 * @component
 * @memberof Components
 * 
 * @param {Object} props - React props
 * @param {string|number|Date} [props.startTime] - The starting time from which elapsed time is calculated. Defaults to the current time if not provided.
 * @param {string|number} [props.fontSize] - The font size of the displayed time.
 * 
 * @returns {JSX.Element} A formatted time display with days, hours, and minutes.
 * 
 * @example
 * <Time startTime="2024-03-01T12:00:00Z" fontSize="24px" />
 */
const Time = ({ startTime, fontSize }) => {
    const initialStartTime = startTime ? new Date(startTime) : new Date();
    const [flash, setFlash] = useState(true);
    /**
   * Calculates the elapsed time since the given start time.
   * 
   * @param {Date} start - The start time.
   * @returns {Object} An object containing days, hours, and minutes.
   */
    const calculateElapsedTime = (start) => {
        const now = new Date();
        const difference = now - start;
        const elapsed = new Date(difference);
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = elapsed.getUTCHours();
        const minutes = elapsed.getUTCMinutes();
        return { days, hours, minutes };
    };
    const [elapsedTime, setElapsedTime] = useState(calculateElapsedTime(initialStartTime));

    /**
     * Formats the elapsed time into a readable string.
     * 
     * @param {Object} time - The elapsed time object.
     * @param {number} time.days - Number of days.
     * @param {number} time.hours - Number of hours.
     * @param {number} time.minutes - Number of minutes.
     * @returns {string} Formatted time string.
     */
    const formatTime = ({ years, months, days, hours, minutes, seconds }) => {
        const formattedHours = String(hours).padStart(2, '0');
        const formattedMinutes = String(minutes).padStart(2, '0');
        const formattedSeconds = String(seconds).padStart(2, '0');
        return `${days} D | ${formattedHours}${flash ? ':' : ' '}${formattedMinutes}`;
    };

    useEffect(() => {
        const intervalId = setInterval(() => {
            setElapsedTime(calculateElapsedTime(initialStartTime));
            setFlash((prevFlash) => !prevFlash);
        }, 1000);

        return () => clearInterval(intervalId);
    }, [initialStartTime]);

    return (
        <div>
            <h1 style={{ fontSize: fontSize, color: 'black', textAlign: 'center', fontWeight: '500' }}>{formatTime(elapsedTime)}</h1>
        </div>
    );
};

export default Time;
